import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { Group } from 'three'

/**
 * Elegant dark chair silhouette — cinematic prop, not a toy box mesh.
 * Soft contact shadow grounds it in the scene.
 */
export function VirtualChair() {
  const group = useRef<Group>(null)
  const shadow = useRef<THREE.Mesh>(null)

  useFrame((state) => {
    if (!group.current) return
    const t = state.clock.elapsedTime
    group.current.position.y = -1.55 + Math.sin(t * 0.45) * 0.012
    group.current.rotation.y = Math.sin(t * 0.12) * 0.03
    if (shadow.current) {
      const mat = shadow.current.material as THREE.MeshBasicMaterial
      mat.opacity = 0.35 + Math.sin(t * 0.45) * 0.02
    }
  })

  const wood = '#0c0c10'
  const woodHi = '#16161c'

  return (
    <group ref={group} position={[0, -1.72, 1.15]} scale={0.92}>
      {/* contact shadow */}
      <mesh ref={shadow} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0.05]}>
        <circleGeometry args={[0.75, 48]} />
        <meshBasicMaterial
          color="#000000"
          transparent
          opacity={0.38}
          depthWrite={false}
        />
      </mesh>

      {/* seat cushion */}
      <mesh position={[0, 0.52, 0.05]} castShadow>
        <boxGeometry args={[0.78, 0.1, 0.72]} />
        <meshStandardMaterial
          color={wood}
          metalness={0.15}
          roughness={0.72}
          envMapIntensity={0.4}
        />
      </mesh>
      <mesh position={[0, 0.58, 0.05]}>
        <boxGeometry args={[0.74, 0.04, 0.68]} />
        <meshStandardMaterial
          color={woodHi}
          metalness={0.1}
          roughness={0.55}
        />
      </mesh>

      {/* curved back suggestion */}
      <mesh position={[0, 1.05, -0.28]}>
        <boxGeometry args={[0.76, 0.95, 0.07]} />
        <meshStandardMaterial color={wood} metalness={0.12} roughness={0.75} />
      </mesh>
      <mesh position={[0, 1.42, -0.22]} rotation={[0.15, 0, 0]}>
        <boxGeometry args={[0.72, 0.12, 0.06]} />
        <meshStandardMaterial color={woodHi} metalness={0.2} roughness={0.5} />
      </mesh>

      {/* arm rests */}
      <mesh position={[-0.42, 0.78, 0.05]}>
        <boxGeometry args={[0.08, 0.08, 0.55]} />
        <meshStandardMaterial color={wood} metalness={0.15} roughness={0.7} />
      </mesh>
      <mesh position={[0.42, 0.78, 0.05]}>
        <boxGeometry args={[0.08, 0.08, 0.55]} />
        <meshStandardMaterial color={wood} metalness={0.15} roughness={0.7} />
      </mesh>
      <mesh position={[-0.42, 0.65, -0.05]}>
        <cylinderGeometry args={[0.03, 0.035, 0.28, 10]} />
        <meshStandardMaterial color={wood} metalness={0.2} roughness={0.65} />
      </mesh>
      <mesh position={[0.42, 0.65, -0.05]}>
        <cylinderGeometry args={[0.03, 0.035, 0.28, 10]} />
        <meshStandardMaterial color={wood} metalness={0.2} roughness={0.65} />
      </mesh>

      {/* legs — tapered dark wood */}
      {(
        [
          [-0.32, 0.26, 0.28],
          [0.32, 0.26, 0.28],
          [-0.32, 0.26, -0.22],
          [0.32, 0.26, -0.22],
        ] as [number, number, number][]
      ).map((p, i) => (
        <mesh key={i} position={p}>
          <cylinderGeometry args={[0.028, 0.04, 0.5, 10]} />
          <meshStandardMaterial color={wood} metalness={0.18} roughness={0.68} />
        </mesh>
      ))}
    </group>
  )
}
